/**
 * Iter123 — Main Hub Granular Permissions.
 *
 * Admin toggles per-feature on/off flags for a role or specific user.
 * Uses the existing `/api/permissions` upsert endpoint. Features list is
 * inline (not hardcoded server-side) so admins can enable module or feature
 * flags without a backend release.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { ShieldCheck, RefreshCw, Save, ChevronDown } from 'lucide-react';

// Iter132 — searchable user picker so admins can target a specific user
// account by name/role instead of memorizing USR-XXXX ids.
function UserPicker({ value, onChange }) {
  const token = localStorage.getItem('nexus_token');
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/users`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const d = await r.json();
          setUsers(Array.isArray(d) ? d : (d.users || []));
        }
      } catch {}
    })();
  }, [token]);
  const selected = users.find(u => u.id === value);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users.slice(0, 40);
    return users.filter(u =>
      (u.id || '').toLowerCase().includes(q) ||
      (u.name || '').toLowerCase().includes(q) ||
      (u.username || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q)
    ).slice(0, 40);
  }, [users, search]);
  return (
    <div className="relative w-72" data-testid="perm-user-picker">
      <button type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 bg-slate-800 border border-slate-600 text-white rounded px-2 py-1.5 text-sm text-left"
        data-testid="perm-target-id">
        <span className="truncate">
          {selected ? `${selected.name || selected.username} · ${selected.id}` : (value || 'Select user…')}
        </span>
        <ChevronDown className="w-4 h-4 opacity-60" />
      </button>
      {open && (
        <div className="absolute left-0 right-0 mt-1 z-50 bg-slate-900 border border-slate-600 rounded shadow-xl max-h-72 overflow-y-auto">
          <div className="p-2 border-b border-slate-700 sticky top-0 bg-slate-900">
            <Input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, username, role, or USR-id"
              className="bg-slate-800 border-slate-600 text-white text-sm"
              data-testid="perm-user-search" />
          </div>
          {filtered.length === 0 && <div className="px-3 py-6 text-sm text-slate-500">No users match.</div>}
          {filtered.map(u => (
            <button type="button" key={u.id}
              onClick={() => { onChange(u.id); setOpen(false); setSearch(''); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-800 border-b border-slate-800 last:border-0"
              data-testid={`perm-user-option-${u.id}`}>
              <div className="text-sm text-white">{u.name || u.username}</div>
              <div className="text-[11px] text-slate-400 font-mono">{u.id} · {(u.role || '').replace(/_/g, ' ')}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const MODULES = [
  'dashboard', 'cases', 'evidence', 'reports', 'ata_warrants', 'clients',
  'field_operations', 'training', 'recruitment', 'skip_trace', 'operations_map',
  'investigator_tracking', 'entity_graph', 'notifications', 'analytics', 'tutorials',
];

const FEATURES = [
  'case.create', 'case.edit', 'case.delete', 'case.close',
  'evidence.upload', 'evidence.download', 'evidence.delete',
  'reports.export_pdf', 'reports.share',
  'ata.create', 'ata.approve', 'ata.sign',
  'field.start_assignment', 'field.review_reports',
  'time.approve', 'time.deny',
  'clients.create', 'clients.remove_access',
  'admin.permissions', 'admin.approvals',
];

const ROLES = [
  'main_hub', 'department_admin', 'field_investigator', 'skip_tracer',
  'process_server', 'trainee', 'field_sergeant', 'lieutenant',
  'captain', 'assistant_chief', 'chief', 'client',
];

export default function AdminMainHubPermissions() {
  const token = localStorage.getItem('nexus_token');
  const H = { Authorization: `Bearer ${token}` };
  const JH = { ...H, 'Content-Type': 'application/json' };

  const [targetType, setTargetType] = useState('role');
  const [targetId, setTargetId] = useState('field_investigator');
  const [modules, setModules] = useState({});
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/permissions/${targetType}/${encodeURIComponent(targetId)}`, { headers: H });
      if (r.ok) {
        const d = await r.json();
        setModules(d?.modules || {});
        setFeatures(d?.features || {});
      } else {
        setModules({}); setFeatures({});
      }
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [targetType, targetId, token]);
  useEffect(() => { if (targetId) load(); }, [load, targetId]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/permissions`, {
        method: 'POST', headers: JH,
        body: JSON.stringify({
          target_type: targetType,
          target_id: targetId,
          module_permissions: modules,
          feature_permissions: features,
        }),
      });
      if (!r.ok) return toast({ title: 'Save failed', variant: 'destructive' });
      toast({ title: 'Permissions saved' });
    } finally { setSaving(false); }
  };

  const handleTargetTypeChange = (v) => {
    setTargetType(v);
    if (v === 'user') setTargetId('');
    if (v === 'role') setTargetId('field_investigator');
    if (v === 'company') setTargetId('');
  };

  const setMod = (k, v) => setModules(prev => ({ ...prev, [k]: !!v }));
  const setFeat = (k, v) => setFeatures(prev => ({ ...prev, [k]: !!v }));

  return (
    <div className="space-y-6" data-testid="main-hub-permissions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-cyan-300" />
          </div>
          <div>
            <div className="text-xs text-cyan-300 uppercase tracking-wider font-mono">Main Hub · Access Control</div>
            <h1 className="text-2xl font-bold text-white">Granular Feature Permissions</h1>
            <p className="text-slate-400 text-sm">Turn any feature on/off for a role or specific user.</p>
          </div>
        </div>
        <Button variant="ghost" onClick={load} className="text-slate-300"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
      </div>

      <Card className="bg-[#131A2A] border-[#334155]">
        <CardHeader><CardTitle className="text-sm text-white">Target</CardTitle></CardHeader>
        <CardContent className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-xs text-slate-400">Target Type</label>
            <Select value={targetType} onValueChange={handleTargetTypeChange}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white w-40" data-testid="perm-target-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="role">Role</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="company">Company</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-400">Target ID / Name</label>
            {targetType === 'role' ? (
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white w-56" data-testid="perm-target-id"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            ) : targetType === 'user' ? (
              <UserPicker value={targetId} onChange={setTargetId} />
            ) : (
              <Input value={targetId} onChange={e => setTargetId(e.target.value)}
                placeholder={'COMP-xxxx'}
                className="bg-slate-800 border-slate-600 text-white w-56" data-testid="perm-target-id" />
            )}
          </div>
          <Button onClick={save} disabled={saving} className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="perm-save-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save Permissions'}
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-[#131A2A] border-[#334155]">
          <CardHeader><CardTitle className="text-sm text-white">Modules</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {MODULES.map(m => (
              <div key={m} className="flex items-center justify-between text-sm text-slate-200 py-1 border-b border-slate-800 last:border-0" data-testid={`perm-module-${m}`}>
                <span>{m.replace(/_/g, ' ')}</span>
                <Switch checked={modules[m] !== false} onCheckedChange={v => setMod(m, v)} data-testid={`perm-module-toggle-${m}`} />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-[#131A2A] border-[#334155]">
          <CardHeader><CardTitle className="text-sm text-white">Features</CardTitle></CardHeader>
          <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
            {FEATURES.map(fk => (
              <div key={fk} className="flex items-center justify-between text-sm text-slate-200 py-1 border-b border-slate-800 last:border-0" data-testid={`perm-feature-${fk}`}>
                <span className="font-mono text-xs">{fk}</span>
                <Switch checked={features[fk] !== false} onCheckedChange={v => setFeat(fk, v)} data-testid={`perm-feature-toggle-${fk}`} />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
